import { InstructionLevel, Auth, navMenu } from "@/types";
import { reactive } from "vue";
import {
  ClientApi,
  SystemApi,
  DepartmentApi,
  Department,
  Occupation,
  Position,
  Role,
  AuthApi,
  Child,
  DepartmentHierarchy
} from "@/autogen/api";

type State = {
  auth: Auth | null;
  accessToken: string | null;
  instruction: {
    level: InstructionLevel;
    message: string;
    callback?: Function;
  };
  isLoading: { state: boolean; message: string };
  occupations: Occupation[] | [];
  departments: Department[] | [];
  allDepartments: Department[] | [];
  positions: Position[] | [];
  role: Role | null;
};

export default class Store {
  private static _instance = new Store();
  static getInstance() {
    return this._instance;
  }
  private state: State = reactive({
    auth: null,
    accessToken: null,
    instruction: { level: InstructionLevel.None, message: "" },
    isLoading: { state: false, message: "" },
    occupations: [],
    departments: [],
    allDepartments: [],
    positions: [],
    role: null
  });
  getAccessToken() {
    return this.state.auth ? this.state.auth.accessToken : localStorage.getItem("accessToken");
  }
  getAuth(): Auth | null {
    return this.state.auth;
  }
  setAuth(auth: Auth | null) {
    this.state.auth = auth;
  }
  getInstruction() {
    return this.state.instruction;
  }
  /**
   * ダイアログ表示
   * @param {InstructionLevel} level ログレベル
   * @param {string | string[]} message ダイアログメッセージ
   * @param {Function} callback? コールバック関数
   * @return void
   */
  setInstructionContent(level: InstructionLevel, message: string | string[], callback?: Function) {
    this.state.instruction.level = level;
    this.state.instruction.message = Array.isArray(message) ? message.join("\n") : message;
    this.state.instruction.callback = callback;
  }
  /**
   * ローディング表示 messageは引数で変更可能 setIsLoading(false)でローディングを停止
   * @param {boolean} isLoading 選択した従業員・応募者
   * @param {string} message? 選択した従業員・応募者
   * @return void
   */
  setIsLoading(isLoading: boolean, message: string = "処理中です") {
    this.state.isLoading.state = isLoading;
    this.state.isLoading.message = message || "";
  }
  getIsLoading() {
    return this.state.isLoading;
  }

  /**
   * call get occupations api
   * @return Promise<void>
   */
  async getOccupationsApi(): Promise<void> {
    this.setOccupations((await new SystemApi().getOccupations({})).data);
  }
  /**
   * occupations setter
   * @param {Occupation[]} occupations 職種
   * @return occupations[]
   */
  private setOccupations(occupations: Occupation[]): void {
    this.state.occupations = occupations;
  }
  /**
   * occupations getter
   * @return occupations
   */
  getOccupations(): Occupation[] | [] {
    if (!this.state.occupations) return [];
    return this.state.occupations.map((e: Occupation) => e);
  }

  /**
   * call get departments api
   * @param {boolean} aim 全ての部署を取得するか制御するフラグ
   * @return Promise<void>
   */
  async getDepartmentsApi({ aim = "" }: { aim?: "" | "all" }): Promise<void> {
    if (aim.length) {
      this.setAllDepartments((await new DepartmentApi().getDepartments({ aim: "all" })).data);
    } else {
      this.setDepartments((await new DepartmentApi().getDepartments({})).data);
    }
  }
  /**
   * departments setter
   * @param {Department[]} departments 部署
   * @return void
   */
  private setDepartments(departments: Department[]): void {
    this.state.departments = departments;
  }
  private setAllDepartments(departments: Department[]): void {
    this.state.allDepartments = departments;
  }
  /**
   * departments getter
   * @param {visibilityMyself?: boolean} ログインユーザーが閲覧可能な部署を取得するか制御するフラグ
   * @param {withoutAll?: boolean} all除外フラグ
   * @return departments
   */
  getDepartments({ visibilityMyself = true, withoutAll = false }: { visibilityMyself?: boolean; withoutAll?: boolean }) {
    const deps = visibilityMyself ? this.state.departments : this.state.allDepartments;
    if (!deps) return [];
    if (withoutAll) return deps.filter(d => d.id !== "all").map((e: Department) => e);
    return deps.map((e: Department) => e);
  }

  /**
   * call get positions api
   * @return Promise<void>
   */
  async getPositionsApi(): Promise<void> {
    this.setPositions((await new ClientApi().getPositions({})).data);
  }
  /**
   * positions setter
   * @param {Position[]} positions 役職
   * @return void
   */
  private setPositions(positions: Position[]): void {
    this.state.positions = positions;
  }
  /**
   * positions getter
   * @return positions
   */
  getPositions(): Position[] {
    if (!this.state.positions) return [];
    return this.state.positions.map((e: Position) => e);
  }

  /**
   * call get positions api
   * @return Promise<void>
   */
  async getRoleApi(): Promise<void> {
    this.setRole((await new AuthApi().getRole({})).data);
  }
  /**
   * role setter
   * @param {Role} role 権限セット
   * @return void
   */
  setRole(role: Role): void {
    this.state.role = role;
  }
  /**
   * role getter
   * @return role
   */
  getRole(): Role {
    if (this.state.role === null) this.getRoleApi();
    return this.state.role!;
  }
  /**
   * create department hierarchy and node paths
   * @param {visibilityMyself?: boolean} ログインユーザーが閲覧可能な部署を取得するか制御するフラグ
   * @param {withoutAll?: boolean} all除外フラグ
   * @return {[departmentHierarchy, _nodePaths]} departmentHierarchy: 部署階層にTree.vueで必要な要素を付与したもの _nodePaths: rootからの(/区切り)部署配列
   * ※state.departmentsの内容は変更しない
   */
  getDepartmentHierarchy({
    visibilityMyself = true,
    withoutAll = true
  }: {
    visibilityMyself?: boolean;
    withoutAll?: boolean;
  }): [typeof departmentHierarchy, typeof _nodePaths] {
    if (!this.getDepartments({})) return [[], []];
    const _nodePaths: { id: string; name: string }[] = [];
    const convertNodes = (arr: Child[], nodePath: string): Child[] => {
      return arr.map((obj: DepartmentHierarchy) => {
        const renewObj = Object.assign({}, obj, {
          id: obj.id,
          text: obj.name,
          state: { expanded: true },
          definition: obj.name
        });
        // set node path from root
        _nodePaths.push({ id: obj.id, name: nodePath + "/" + obj.name });
        if (obj.child && obj.child.length) {
          renewObj.child = convertNodes(obj.child, nodePath + "/" + obj.name);
        }
        return renewObj;
      });
    };
    const departmentHierarchy = this.getDepartments({ visibilityMyself, withoutAll }).map((r: DepartmentHierarchy) => {
      const renew = Object.assign({}, r, {
        id: r.id,
        text: r.name,
        state: { expanded: true },
        definition: r.name
      });
      // set node path from root
      _nodePaths.push({ id: r.id, name: r.name });
      if (r.child && r.child.length) {
        renew.child = convertNodes(r.child, r.name);
      }
      return renew;
    });
    return [departmentHierarchy, _nodePaths];
  }

  /**
   * 部署階層パスをトップ階層と下からn階層以外を中点で省略する
   * e.g. New Business/HYOUMANBOX/フロントエンド/チーム① → New Business/.../フロントエンド/チーム①
   * @param {level} 下n階層の値 e.g. -2の場合下から2階層
   * @returns {string[]} 部署階層を省略した配列
   */
  getShortDepartmentsList({
    visibilityMyself = true,
    withoutAll = true,
    level = -2
  }: {
    visibilityMyself?: boolean;
    withoutAll?: boolean;
    level?: number;
  }): string[] {
    if (level > -1) level = -2;
    return this.getDepartmentHierarchy({ visibilityMyself, withoutAll })[1].map((department: Department) => {
      if ((department.name.match(/\//g) || []).length > 2) {
        const replacePath =
          "/...../" +
          department.name
            .split("/")!
            .slice(level)
            .join("/");
        return Object.assign({
          id: department.id,
          name: department.name.replace(/\/.*/i, replacePath)
        });
      }
      return department;
    });
  }
  /**
   * get department path by departmentId
   *
   * @param {string} departmentId 部署ID
   * @return string
   */
  getDepartmentPath(departmentId: string) {
    var departmentName = "全社";
    this.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1].map((department: Department) => {
      if (department.id == departmentId) departmentName = department.name;
    });
    return departmentName;
  }
}
